import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useView } from '../context/ViewContext';
import './ProfileSettings.css';

const ProfileSettings = ({ isOpen, onClose }) => {
    const { user, refreshUser } = useView();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState('basic');

    const [formData, setFormData] = useState({
        name: '',
        contact_no: '',
        location: '',
        latitude: '',
        longitude: '',
        opens_at: '',
        closes_at: '',
        operating_days: '',
        service_radius_km: '',
        // Manufacturer-specific
        num_machines: '',
        usable_area_sqft: '',
        num_workers: '',
        available_shifts: '',
        capacity: ''
    });

    // Fetch current user details when panel opens
    useEffect(() => {
        if (isOpen && user?.id) {
            fetchUserDetails();
        }
    }, [isOpen, user?.id]);

    const fetchUserDetails = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axios.get(
                `https://rotica.in/get_entity_details.php?entity_type=${user.role}&entity_id=${user.id}`
            );

            if (response.data.success) {
                const data = response.data.entity;
                setFormData({
                    name: data.name || '',
                    contact_no: data.phone || data.contact_no || '', // Handle phone from DB
                    location: data.location || '',
                    latitude: data.latitude || '',
                    longitude: data.longitude || '',
                    opens_at: data.opens_at || '',
                    closes_at: data.closes_at || '',
                    operating_days: data.operating_days || '',
                    service_radius_km: data.service_radius_km || '',
                    num_machines: data.num_machines || '',
                    usable_area_sqft: data.usable_area_sqft || '',
                    num_workers: data.num_workers || '',
                    available_shifts: data.available_shifts || '',
                    capacity: data.capacity || ''
                });
            } else {
                setError(response.data.message || 'Failed to load profile');
            }
        } catch (err) {
            console.error(err);
            setError('Network error loading profile');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
        setSuccess('');
    };

    const handleShiftToggle = (shiftTime) => {
        const currentShifts = formData.available_shifts ? formData.available_shifts.split(',') : [];
        const idx = currentShifts.indexOf(shiftTime);
        if (idx > -1) {
            currentShifts.splice(idx, 1);
        } else {
            currentShifts.push(shiftTime);
        }
        setFormData({ ...formData, available_shifts: currentShifts.filter(s => s).join(',') });
    };

    const getLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setFormData({
                        ...formData,
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    });
                    setSuccess("Location updated!");
                },
                (error) => {
                    console.error(error);
                    setError("Unable to retrieve location");
                }
            );
        } else {
            setError("Geolocation not supported");
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');
            setSuccess('');

            const data = new FormData();
            data.append('entity_type', user.role);
            data.append('entity_id', user.id);

            Object.keys(formData).forEach(key => {
                data.append(key, formData[key]);
            });

            const response = await axios.post('https://rotica.in/update_entity_details.php', data);

            if (response.data.success) {
                setSuccess('Profile updated successfully!');
                // Update local storage with new name
                const updatedUser = { ...user, name: formData.name };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                if (refreshUser) refreshUser();

                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(response.data.message || 'Update failed');
            }
        } catch (err) {
            console.error(err);
            setError('Network error saving profile');
        } finally {
            setSaving(false);
        }
    };

    const isManufacturer = user?.role === 'manufacturer';

    return (
        <>
            {/* Backdrop */}
            <div
                className={`profile-backdrop ${isOpen ? 'visible' : ''}`}
                onClick={onClose}
            />

            {/* Slide-out Panel */}
            <div className={`profile-panel ${isOpen ? 'open' : ''}`}>
                <div className="profile-panel-header">
                    <h2>Profile Settings</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                {loading ? (
                    <div className="profile-loading">
                        <div className="spinner"></div>
                        <p>Loading profile...</p>
                    </div>
                ) : (
                    <>
                        {/* Tab Navigation */}
                        <div className="profile-tabs">
                            <button
                                className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
                                onClick={() => setActiveTab('basic')}
                            >
                                Basic Info
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'operations' ? 'active' : ''}`}
                                onClick={() => setActiveTab('operations')}
                            >
                                Operations
                            </button>
                            {isManufacturer && (
                                <button
                                    className={`tab-btn ${activeTab === 'manufacturing' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('manufacturing')}
                                >
                                    Manufacturing
                                </button>
                            )}
                        </div>

                        <div className="profile-content">
                            {/* Basic Info Tab */}
                            {activeTab === 'basic' && (
                                <div className="tab-content animate-fade-in">
                                    <div className="form-group">
                                        <label>Company / Name</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="Enter company name"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Contact Number</label>
                                        <input
                                            type="tel"
                                            name="contact_no"
                                            value={formData.contact_no}
                                            onChange={handleChange}
                                            placeholder="Enter contact number"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Location</label>
                                        <input
                                            type="text"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleChange}
                                            placeholder="Enter location/address"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>GPS Coordinates</label>
                                        <div className="coordinates-row">
                                            <input
                                                type="text"
                                                name="latitude"
                                                value={formData.latitude}
                                                onChange={handleChange}
                                                placeholder="Latitude"
                                                readOnly
                                            />
                                            <input
                                                type="text"
                                                name="longitude"
                                                value={formData.longitude}
                                                onChange={handleChange}
                                                placeholder="Longitude"
                                                readOnly
                                            />
                                        </div>
                                        <button type="button" className="location-btn" onClick={getLocation}>
                                            📍 Update Location
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Operations Tab */}
                            {activeTab === 'operations' && (
                                <div className="tab-content animate-fade-in">
                                    <div className="form-row">
                                        <div className="form-group half">
                                            <label>Opens At</label>
                                            <input
                                                type="time"
                                                name="opens_at"
                                                value={formData.opens_at}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="form-group half">
                                            <label>Closes At</label>
                                            <input
                                                type="time"
                                                name="closes_at"
                                                value={formData.closes_at}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Operating Days</label>
                                        <input
                                            type="text"
                                            name="operating_days"
                                            value={formData.operating_days}
                                            onChange={handleChange}
                                            placeholder="e.g., Mon,Tue,Wed,Thu,Fri"
                                        />
                                        <small className="hint">Comma-separated day names</small>
                                    </div>
                                    <div className="form-group">
                                        <label>Service Radius (km)</label>
                                        <input
                                            type="number"
                                            name="service_radius_km"
                                            value={formData.service_radius_km}
                                            onChange={handleChange}
                                            placeholder="Enter service radius"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Manufacturing Tab (for manufacturers only) */}
                            {activeTab === 'manufacturing' && isManufacturer && (
                                <div className="tab-content animate-fade-in">
                                    <div className="form-row">
                                        <div className="form-group half">
                                            <label>Number of Machines</label>
                                            <input
                                                type="number"
                                                name="num_machines"
                                                value={formData.num_machines}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="form-group half">
                                            <label>Number of Workers</label>
                                            <input
                                                type="number"
                                                name="num_workers"
                                                value={formData.num_workers}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Usable Area (sq ft)</label>
                                        <input
                                            type="number"
                                            name="usable_area_sqft"
                                            value={formData.usable_area_sqft}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Capacity (in 100's)</label>
                                        <input
                                            type="number"
                                            name="capacity"
                                            value={formData.capacity}
                                            onChange={handleChange}
                                            placeholder="e.g., 5 = 500 units"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Available Shifts</label>
                                        <div className="shift-options">
                                            <label className="shift-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.available_shifts?.includes('7-10am')}
                                                    onChange={() => handleShiftToggle('7-10am')}
                                                />
                                                <span>Batch 1: 7-10 AM</span>
                                            </label>
                                            <label className="shift-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.available_shifts?.includes('12-3pm')}
                                                    onChange={() => handleShiftToggle('12-3pm')}
                                                />
                                                <span>Batch 2: 12-3 PM</span>
                                            </label>
                                            <label className="shift-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.available_shifts?.includes('6-9pm')}
                                                    onChange={() => handleShiftToggle('6-9pm')}
                                                />
                                                <span>Batch 3: 6-9 PM</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Messages */}
                        {error && <div className="profile-error">{error}</div>}
                        {success && <div className="profile-success">{success}</div>}

                        {/* Save Button */}
                        <div className="profile-actions">
                            <button
                                className="save-profile-btn"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default ProfileSettings;
